import { Component } from 'react';
import { Modal, Button } from 'semantic-ui-react';

export default class ExportPreviewModal extends Component {
    constructor(props) {
        super(props);
        this.state = {
            sortColumn: 'level',
            sortDirection: 'asc',
            reconColumns: { bcare: true, procBasedPay: true },
            reconResults: {},   // L1 vs L2, keyed by provider|abbr
            reconResults3: {},  // L2 vs L3, keyed by provider|abbr|practice
            reconRun: false,
            mismatchDetail: null,
        };
    }

    componentDidUpdate(prevProps) {
        if (prevProps.exportPreviewData !== this.props.exportPreviewData) {
            this.setState({ reconRun: false, reconResults: {}, reconResults3: {}, mismatchDetail: null });
        }
    }

    handleSortColumn = (column) => {
        const direction = this.state.sortColumn === column && this.state.sortDirection === 'asc' ? 'desc' : 'asc';
        this.setState({ sortColumn: column, sortDirection: direction });
    };

    getSortedData = () => {
        const { exportPreviewData = [] } = this.props;
        const { sortColumn, sortDirection } = this.state;
        return [...exportPreviewData].sort((a, b) => {
            const aVal = a[sortColumn];
            const bVal = b[sortColumn];
            const cmp = typeof aVal === 'number' ? aVal - bVal : String(aVal).localeCompare(String(bVal));
            return sortDirection === 'asc' ? cmp : -cmp;
        });
    };

    handleReconColumnToggle = (column) => {
        this.setState(prev => ({ reconColumns: { ...prev.reconColumns, [column]: !prev.reconColumns[column] } }));
    };

    runReconciliation = () => {
        const { exportPreviewData = [] } = this.props;
        const { reconColumns } = this.state;
        const reconResults = {};
        const reconResults3 = {};
        const grouped12 = {};
        const grouped23 = {};

        exportPreviewData.forEach(row => {
            const key12 = `${row.provider}|${row.abbr}`;
            const key23 = `${row.provider}|${row.abbr}|${row.practice}`;
            if (!grouped12[key12]) {
                grouped12[key12] = { level1: null, level2: [], provider: row.provider, abbr: row.abbr, contractType: '' };
            }
            if (row.level === 1) {
                grouped12[key12].level1 = row;
                grouped12[key12].contractType = row.contractType || '';
            } else if (row.level === 2) {
                grouped12[key12].level2.push(row);
                if (!grouped23[key23]) {
                    grouped23[key23] = { level2: null, level3: [], provider: row.provider, abbr: row.abbr, practice: row.practice, contractType: row.contractType || '' };
                }
                grouped23[key23].level2 = row;
            } else if (row.level === 3) {
                if (!grouped23[key23]) {
                    grouped23[key23] = { level2: null, level3: [], provider: row.provider, abbr: row.abbr, practice: row.practice, contractType: row.contractType || '' };
                }
                grouped23[key23].level3.push(row);
            }
        });

        const checkFields = (parentRow, childRows, parentKey, childKey) => {
            let allPassed = true;
            const details = {};
            if (reconColumns.bcare) {
                const parentVal = parseFloat(parentRow.bcare);
                const childSum = childRows.reduce((s, r) => s + parseFloat(r.bcare || 0), 0);
                const match = Math.abs(parentVal - childSum) < 0.01;
                details.bcare = { match, [parentKey]: parentVal, [childKey]: childSum, diff: parentVal - childSum };
                if (!match) allPassed = false;
            }
            if (reconColumns.procBasedPay) {
                const parentVal = parseFloat(parentRow.procBasedPay);
                const childSum = childRows.reduce((s, r) => s + parseFloat(r.procBasedPay || 0), 0);
                const match = Math.abs(parentVal - childSum) < 0.01;
                details.procBasedPay = { match, [parentKey]: parentVal, [childKey]: childSum, diff: parentVal - childSum };
                if (!match) allPassed = false;
            }
            return { allPassed, details };
        };

        Object.entries(grouped12).forEach(([key, g]) => {
            if (!g.level1 || g.level2.length === 0) {
                reconResults[key] = { passed: true, details: {}, provider: g.provider, abbr: g.abbr, contractType: g.contractType };
                return;
            }
            const { allPassed, details } = checkFields(g.level1, g.level2, 'l1Val', 'l2Sum');
            reconResults[key] = { passed: allPassed, details, provider: g.provider, abbr: g.abbr, contractType: g.contractType };
        });

        Object.entries(grouped23).forEach(([key, g]) => {
            if (!g.level2 || g.level3.length === 0) {
                reconResults3[key] = { passed: true, details: {}, provider: g.provider, abbr: g.abbr, practice: g.practice, contractType: g.contractType };
                return;
            }
            const { allPassed, details } = checkFields(g.level2, g.level3, 'l2Val', 'l3Sum');
            reconResults3[key] = { passed: allPassed, details, provider: g.provider, abbr: g.abbr, practice: g.practice, contractType: g.contractType };
        });

        this.setState({ reconResults, reconResults3, reconRun: true, mismatchDetail: null });
    };

    getRowReconStatus = (row) => {
        if (!this.state.reconRun || row.level === 3) {
            return { backgroundColor: 'transparent', indicator: '', failedColumns: '', key: null };
        }
        const key = row.level === 1
            ? `${row.provider}|${row.abbr}`
            : `${row.provider}|${row.abbr}|${row.practice}`;
        const result = row.level === 1 ? this.state.reconResults[key] : this.state.reconResults3[key];
        if (!result) return { backgroundColor: 'transparent', indicator: '', failedColumns: '', key: null };
        if (result.passed) return { backgroundColor: '#d4edda', indicator: '✓', failedColumns: '', key, level: row.level };

        const failed = [];
        if (this.state.reconColumns.bcare && result.details.bcare && !result.details.bcare.match) failed.push('B.Care');
        if (this.state.reconColumns.procBasedPay && result.details.procBasedPay && !result.details.procBasedPay.match) failed.push('Proc Based Pay');
        return { backgroundColor: '#f8d7da', indicator: '✗', failedColumns: failed.join(', '), key, level: row.level, result };
    };

    handleMismatchClick = (reconStatus, row) => {
        if (reconStatus.indicator !== '✗') return;
        const result = row.level === 1 ? this.state.reconResults[reconStatus.key] : this.state.reconResults3[reconStatus.key];
        if (!result) return;
        this.setState({
            mismatchDetail: {
                key: reconStatus.key,
                level: row.level,
                provider: result.provider,
                abbr: result.abbr,
                practice: result.practice || '',
                contractType: result.contractType || row.contractType || '',
                details: result.details,
            }
        });
    };

    exportMismatches = () => {
        const { reconResults, reconResults3, reconRun } = this.state;
        if (!reconRun) return;

        const rows = [['Level', 'Provider', 'ABBR', 'Contract', 'Practice', 'Check', 'Field', 'Parent Value', 'Children Sum', 'Diff']];

        Object.entries(reconResults).forEach(([, r]) => {
            if (r.passed) return;
            Object.entries(r.details || {}).forEach(([field, d]) => {
                if (!d.match) rows.push([
                    1, r.provider, r.abbr, r.contractType, '',
                    'L1 vs L2', field === 'bcare' ? 'B.Care' : 'Proc Based Pay',
                    d.l1Val?.toFixed(3), d.l2Sum?.toFixed(3), d.diff?.toFixed(3)
                ]);
            });
        });

        Object.entries(reconResults3).forEach(([, r]) => {
            if (r.passed) return;
            Object.entries(r.details || {}).forEach(([field, d]) => {
                if (!d.match) rows.push([
                    2, r.provider, r.abbr, r.contractType, r.practice,
                    'L2 vs L3', field === 'bcare' ? 'B.Care' : 'Proc Based Pay',
                    d.l2Val?.toFixed(3), d.l3Sum?.toFixed(3), d.diff?.toFixed(3)
                ]);
            });
        });

        if (rows.length === 1) {
            alert('No mismatches found to export.');
            return;
        }

        const csv = rows.map(r => r.map(v => `"${v ?? ''}"`).join(',')).join('\n');
        const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
        const a = document.createElement('a');
        a.href = url;
        a.download = 'recon_mismatches.csv';
        a.click();
        URL.revokeObjectURL(url);
    };

    renderSortIndicator(column) {
        if (this.state.sortColumn !== column) return null;
        return this.state.sortDirection === 'asc' ? ' ↑' : ' ↓';
    }

    renderMismatchDetail() {
        const { mismatchDetail } = this.state;
        if (!mismatchDetail) return null;
        const { level, provider, abbr, practice, contractType, details } = mismatchDetail;
        const isL1 = level === 1;
        const parentLabel = isL1 ? 'L1 Provider Total' : 'L2 Practice Total';
        const childLabel = isL1 ? 'Sum of L2 (Practices)' : 'Sum of L3 (Days)';
        const tdD = { padding: '4px 8px', border: '1px solid #e0c97a' };
        const thD = { ...tdD, fontWeight: 'bold', backgroundColor: '#ffeaa0' };

        return (
            <div style={{ margin: '10px 0', padding: '12px', backgroundColor: '#fff9e6', border: '1px solid #ffc107', borderRadius: '4px', fontSize: '13px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <strong>Mismatch Detail — {isL1 ? 'L1 vs L2 (Provider vs Practices)' : 'L2 vs L3 (Practice vs Days)'}</strong>
                    <span style={{ cursor: 'pointer', fontWeight: 'bold', padding: '0 6px', fontSize: '16px' }} onClick={() => this.setState({ mismatchDetail: null })}>✕</span>
                </div>
                <div style={{ marginBottom: '8px' }}>
                    <strong>Provider:</strong> {provider} ({abbr})
                    &nbsp;|&nbsp; <strong>Contract:</strong> {contractType || '—'}
                    {practice && <span>&nbsp;|&nbsp; <strong>Practice:</strong> {practice}</span>}
                </div>
                <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                    <thead>
                        <tr>
                            <th style={thD}>Field</th>
                            <th style={{ ...thD, textAlign: 'right' }}>{parentLabel}</th>
                            <th style={{ ...thD, textAlign: 'right' }}>{childLabel}</th>
                            <th style={{ ...thD, textAlign: 'right' }}>Diff</th>
                            <th style={{ ...thD, textAlign: 'center' }}>Match</th>
                        </tr>
                    </thead>
                    <tbody>
                        {details.bcare && (
                            <tr style={{ backgroundColor: details.bcare.match ? '#d4edda' : '#f8d7da' }}>
                                <td style={tdD}>B.Care</td>
                                <td style={{ ...tdD, textAlign: 'right' }}>{(isL1 ? details.bcare.l1Val : details.bcare.l2Val)?.toFixed(3)}</td>
                                <td style={{ ...tdD, textAlign: 'right' }}>{(isL1 ? details.bcare.l2Sum : details.bcare.l3Sum)?.toFixed(3)}</td>
                                <td style={{ ...tdD, textAlign: 'right' }}>{details.bcare.diff?.toFixed(3)}</td>
                                <td style={{ ...tdD, textAlign: 'center' }}>{details.bcare.match ? '✓' : '✗'}</td>
                            </tr>
                        )}
                        {details.procBasedPay && (
                            <tr style={{ backgroundColor: details.procBasedPay.match ? '#d4edda' : '#f8d7da' }}>
                                <td style={tdD}>Proc Based Pay</td>
                                <td style={{ ...tdD, textAlign: 'right' }}>${(isL1 ? details.procBasedPay.l1Val : details.procBasedPay.l2Val)?.toFixed(2)}</td>
                                <td style={{ ...tdD, textAlign: 'right' }}>${(isL1 ? details.procBasedPay.l2Sum : details.procBasedPay.l3Sum)?.toFixed(2)}</td>
                                <td style={{ ...tdD, textAlign: 'right' }}>${details.procBasedPay.diff?.toFixed(2)}</td>
                                <td style={{ ...tdD, textAlign: 'center' }}>{details.procBasedPay.match ? '✓' : '✗'}</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        );
    }

    render() {
        const { open, onClose, isProgressValue, onProceedExport } = this.props;
        const { reconColumns, reconRun } = this.state;
        const sortedData = this.getSortedData();
        const thStyle = { border: '1px solid #ddd', padding: '8px', cursor: 'pointer', fontWeight: 'bold' };
        const tdStyle = { border: '1px solid #ddd', padding: '8px' };

        return (
            <Modal open={open} onClose={onClose} size='large'>
                <Modal.Header>Export Preview - Provider and B.Care Data</Modal.Header>
                <Modal.Content scrolling>
                    <div style={{ marginBottom: '15px', padding: '10px', backgroundColor: '#f9f9f9', borderRadius: '4px' }}>
                        <div style={{ marginBottom: '10px' }}>
                            <strong>Reconciliation Columns:</strong>
                            <label style={{ marginRight: '20px', marginLeft: '10px' }}>
                                <input type='checkbox' checked={reconColumns.bcare} onChange={() => this.handleReconColumnToggle('bcare')} />
                                {' B.Care'}
                            </label>
                            <label>
                                <input type='checkbox' checked={reconColumns.procBasedPay} onChange={() => this.handleReconColumnToggle('procBasedPay')} />
                                {' Proc Based Pay'}
                            </label>
                        </div>
                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                            <Button size='small' onClick={this.runReconciliation}>Run Reconciliation</Button>
                            {reconRun && (
                                <Button size='small' color='orange' onClick={this.exportMismatches}>Export Mismatches</Button>
                            )}
                        </div>
                        {reconRun && (
                            <div style={{ marginTop: '10px', fontSize: '12px' }}>
                                <span style={{ display: 'inline-block', marginRight: '15px' }}>
                                    <span style={{ display: 'inline-block', width: '12px', height: '12px', backgroundColor: '#d4edda', marginRight: '5px' }} />
                                    Passed
                                </span>
                                <span>
                                    <span style={{ display: 'inline-block', width: '12px', height: '12px', backgroundColor: '#f8d7da', marginRight: '5px' }} />
                                    Failed — click ✗ for details
                                </span>
                            </div>
                        )}
                    </div>

                    {this.renderMismatchDetail()}

                    <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr>
                                    <th style={{ ...thStyle, textAlign: 'left' }} onClick={() => this.handleSortColumn('level')}>Level{this.renderSortIndicator('level')}</th>
                                    <th style={{ ...thStyle, textAlign: 'left' }} onClick={() => this.handleSortColumn('provider')}>Provider{this.renderSortIndicator('provider')}</th>
                                    <th style={{ ...thStyle, textAlign: 'left' }} onClick={() => this.handleSortColumn('abbr')}>ABBR{this.renderSortIndicator('abbr')}</th>
                                    <th style={{ ...thStyle, textAlign: 'left' }} onClick={() => this.handleSortColumn('contractType')}>Contract{this.renderSortIndicator('contractType')}</th>
                                    <th style={{ ...thStyle, textAlign: 'left' }} onClick={() => this.handleSortColumn('practice')}>Practice{this.renderSortIndicator('practice')}</th>
                                    <th style={{ ...thStyle, textAlign: 'right' }} onClick={() => this.handleSortColumn('bcare')}>B.Care{this.renderSortIndicator('bcare')}</th>
                                    <th style={{ ...thStyle, textAlign: 'right' }} onClick={() => this.handleSortColumn('procBasedPay')}>Proc Based Pay{this.renderSortIndicator('procBasedPay')}</th>
                                    <th style={{ ...thStyle, textAlign: 'left' }}>Recon</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedData.map((row, idx) => {
                                    const reconStatus = this.getRowReconStatus(row);
                                    const rowBg = row.level === 3 ? '#fafafa' : reconStatus.backgroundColor;
                                    return (
                                        <tr key={idx} style={{ backgroundColor: rowBg, opacity: row.level === 3 ? 0.7 : 1 }}>
                                            <td style={{ ...tdStyle, color: row.level === 3 ? '#aaa' : 'inherit' }}>{row.level}</td>
                                            <td style={tdStyle}>{row.provider}</td>
                                            <td style={tdStyle}>{row.abbr}</td>
                                            <td style={tdStyle}>{row.contractType || '—'}</td>
                                            <td style={tdStyle}>{row.level === 1 ? '—' : row.practice}</td>
                                            <td style={{ ...tdStyle, textAlign: 'right' }}>{row.bcare}</td>
                                            <td style={{ ...tdStyle, textAlign: 'right' }}>${row.procBasedPay}</td>
                                            <td
                                                style={{
                                                    ...tdStyle,
                                                    fontSize: '12px',
                                                    color: reconStatus.failedColumns ? '#d32f2f' : '#2e7d32',
                                                    cursor: reconStatus.indicator === '✗' ? 'pointer' : 'default',
                                                    userSelect: 'none',
                                                }}
                                                onClick={() => this.handleMismatchClick(reconStatus, row)}
                                                title={reconStatus.indicator === '✗' ? 'Click to see mismatch details' : ''}
                                            >
                                                {reconStatus.indicator}{reconStatus.failedColumns ? ` — ${reconStatus.failedColumns}` : ''}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </Modal.Content>
                <Modal.Actions>
                    <Button onClick={onClose}>Cancel</Button>
                    <Button primary onClick={onProceedExport} loading={isProgressValue > 0 && isProgressValue < 100}>
                        Proceed with Export
                    </Button>
                </Modal.Actions>
            </Modal>
        );
    }
}
